/**
 * Shared font-loading types. Framework-agnostic so the live preview, the stores and the future
 * template design tool can all consume the same module.
 */

/** One `@font-face` the server publishes. `family` is the Windows/GDI legacy family name (TrueType name ID 1). */
export interface FontManifestFace {
  family: string;
  /**
   * Other names templates may use for this same file (full name, PostScript name, "family subfamily").
   * The loader registers the face under each of them too, so `Tfont` still maps 1:1.
   */
  aliases?: string[];
  /** 400 or 700 */
  weight: number;
  /** 'normal' | 'italic' */
  style: string;
  url: string;
}

export interface FontManifest {
  /** Changes whenever any enabled face changes. */
  version: string;
  faces: FontManifestFace[];
}

/**
 * DP2 stores the three style checkboxes of a text node as a bitmask in `Tstyle`.
 * Verified against real templates: none = 0, Bold = 1, Bold+Italic = 3, Bold+Underline = 5,
 * Italic+Underline = 6, all three = 7.
 */
export const DP2_TSTYLE = {
  BOLD: 1,
  ITALIC: 2,
  UNDERLINE: 4,
} as const;

export type KonvaFontStyle = 'normal' | 'bold' | 'italic' | 'italic bold';

/** What a DP2 text node's `Tfont` + `Tstyle` resolve to for Konva and canvas measurement. */
export interface ResolvedFont {
  /** CSS family name, unquoted. Falls back to 'sans-serif' when the template names none. */
  fontFamily: string;
  fontStyle: KonvaFontStyle;
  textDecoration: '' | 'underline';
  bold: boolean;
  italic: boolean;
  underline: boolean;
  /** Whether the manifest (once loaded) has at least one face for this family. */
  isServable: boolean;
}

export interface EnsureFontsOptions {
  /**
   * Give up waiting after this many milliseconds. The fonts keep loading in the background and
   * `onFontsChanged` still fires when they land. `0`/undefined means wait indefinitely.
   */
  timeoutMs?: number;
}

export interface EnsureFontsResult {
  /** Families whose faces all finished loading before we returned (includes `alreadyLoaded`). */
  loaded: string[];
  /**
   * Families whose faces had already finished loading before this call (e.g. from a job-level
   * preload). Nothing was fetched for these; the registry handed back the completed load.
   */
  alreadyLoaded: string[];
  /** Families the manifest has no faces for (nothing to load; the browser will substitute). */
  missing: string[];
  /** Families with at least one face that failed to load. */
  failed: string[];
  /** True when `timeoutMs` elapsed before every face settled. */
  timedOut: boolean;
  /** True when the manifest itself could not be fetched; every requested family is then in `missing`. */
  manifestUnavailable: boolean;
}

export interface JobFontsDisplayModel {
  fonts: string[];
}
